/* eslint-disable jsx-a11y/control-has-associated-label,react-hooks/exhaustive-deps */
import { useTranslation } from 'react-i18next';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import { useHistory } from 'react-router-dom';
import { OrderCardContext } from '../../../../../contexts/card/cardOrder/context';
import {
  CARD_MONAVATE_REPORT_LOST,
  CARD_MONAVATE_REPORT_STOLEN,
  CARD_PGW_PAYMENT_ORDER,
} from '../../../mutations';
import { FLOWS, sendStats } from '../../../../../services/mixpanel';
import { MXP_CARD_ORDER_CONFIRM, MXP_CARD_ORDER_PAYMENT_METHOD } from '../../mixpanel-events';
import {
  CardPaymentMethods,
  cardProviders,
  mainCardTypes,
  orderCardFlowScreens,
} from '../../../constants';
import { showAlert } from '../../../../../utilities/notifications';
import { REPORT_CARD_STATUS } from '../../../../../utilities/constants';
import { Button } from '../../../../../components';
import ModalBodyLayout from '../partials/MainBodyLayout';
import { GenericLoading } from '../../../../../components/Placeholders';
import PgwIframe from '../../../../../components/PgwIframe';
import { useCardStatus } from '../../../../../utilities/sharedHooks/user';

const ProceedWithPayment = () => {
  const { t: translate } = useTranslation();
  const history = useHistory();
  const { state, openScreen } = useContext(OrderCardContext);

  const [pgwUrl, setPgwUrl] = useState();
  const [showPgwFooter, setShowPgwFooter] = useState(false);

  const uiFeatureShowHint = state?.cardOrderSettings?.ui?.ui_feature_show_hints;
  const cardCurrency = state?.cardCurrency;
  const cardType = state?.cardType;
  const cardProvider = state?.cardProvider;
  const mainCardType = state?.mainCardType;
  const userRegionSettings = state?.regionSettings;

  const isCardReplacement = state?.isCardReplacement;
  const cardIssueOptions = state?.cardIssueOptions;

  const { refetch: refetchCardStatus } = useCardStatus({
    enabled: false,
  });

  const handleClick = () => {
    if (uiFeatureShowHint) {
      history.push('/hint/card');
    }
  };

  const { mutate: orderCardPgw, isLoading } = useMutation(CARD_PGW_PAYMENT_ORDER, {
    onSettled: (data, error) => {
      sendStats(
        MXP_CARD_ORDER_PAYMENT_METHOD,
        {
          paymentMethod: CardPaymentMethods.PGW,
          cardCurrency: cardCurrency.coin,
          isBalanceEnough: undefined,
          cardProvider: cardType,
        },
        FLOWS.CARD_ORDER
      );
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      } else if (data && data.redirectUrl) {
        setPgwUrl(data.redirectUrl);
      }
    },
  });

  const reportCardKey = useMemo(() => {
    if (cardIssueOptions) {
      if (cardIssueOptions?.state === REPORT_CARD_STATUS.STOLEN) {
        return CARD_MONAVATE_REPORT_STOLEN;
      }
      if (cardIssueOptions?.state === REPORT_CARD_STATUS.LOST) {
        return CARD_MONAVATE_REPORT_LOST;
      }
    }

    return null;
  }, [cardIssueOptions]);

  const { mutate: reportMastercardCard, isLoading: isLoadingReportCard } = useMutation(
    reportCardKey,
    {
      onSettled: (data, error) => {
        if (error) {
          showAlert({ message: error.message, type: 'danger' });
        } else if (data && data.redirectUrl) {
          setPgwUrl(data.redirectUrl);
        }
      },
    }
  );

  useEffect(() => {
    if (
      isCardReplacement &&
      cardType === cardProviders.MONAVATE &&
      cardProvider === cardProviders.MONAVATE
    ) {
      reportMastercardCard({
        isPremium: false,
        currency: cardCurrency.coin,
        cardProvider: cardType,
        isVirtual: mainCardType === mainCardTypes.VIRTUAL,
        isFiatPayment: true,
      });
    } else {
      orderCardPgw({
        isPremium: false,
        currency: cardCurrency.coin,
        cardProvider: cardType,
        isVirtual: mainCardType === mainCardTypes.VIRTUAL,
      });
    }
  }, []);

  const closeCardOrder = () => history.push('/dashboard/card');

  const handleResolvePgw = (isConfirmed) => {
    if (!isCardReplacement) {
      sendStats(
        MXP_CARD_ORDER_CONFIRM,
        { error: !isConfirmed, data: !!isConfirmed, cardProvider: cardType },
        FLOWS.CARD_ORDER
      );
    }
    if (!isConfirmed) {
      setShowPgwFooter(false);
    } else if (
      isConfirmed &&
      cardType === cardProviders.MONAVATE &&
      mainCardType === mainCardTypes.VIRTUAL
    ) {
      showAlert({
        message: translate('mastercard_virtual_request_success'),
        type: 'success',
        showIcon: !userRegionSettings?.allow_manual_card_order,
        blockAutoDismiss: !userRegionSettings?.allow_manual_card_order,
        onClick: userRegionSettings?.allow_manual_card_order ? () => {} : handleClick,
      });
      refetchCardStatus();
    } else if (
      isConfirmed &&
      cardType === cardProviders.MONAVATE &&
      mainCardType === mainCardTypes.PHYSICAL
    ) {
      showAlert({
        message: translate('mastercard_monavate_request_success'),
        type: 'success',
        blockAutoDismiss: true,
        showIcon: true,
      });
      refetchCardStatus();
    } else if (isConfirmed && cardType === cardProviders.GALILEO) {
      showAlert({
        message:
          mainCardType === mainCardTypes.VIRTUAL
            ? translate('us_virtual_card_request_success')
            : translate('us_card_request_success'),
        type: 'success',
        blockAutoDismiss: true,
        showIcon: true,
        onClick: handleClick,
      });
      refetchCardStatus();
      closeCardOrder();
    } else {
      refetchCardStatus();
      setShowPgwFooter(true);
    }
  };

  const renderFooter = () => {
    if (pgwUrl && showPgwFooter) {
      return (
        <div className="btn-container">
          <Button className="w-100" variant="primary" onClick={closeCardOrder}>
            {translate('continue_btn_title')}
          </Button>
        </div>
      );
    }
    return <></>;
  };

  return (
    <ModalBodyLayout
      withBack
      onPrevClick={() => openScreen(orderCardFlowScreens.SELECT_PAYMENT_TYPE)}
      title={translate('card_order_proceed_with_payment_title')}>
      {isLoading || isLoadingReportCard ? (
        <GenericLoading />
      ) : (
        <PgwIframe pgwUrl={pgwUrl} onPgwResolve={handleResolvePgw} />
      )}
      {renderFooter()}
    </ModalBodyLayout>
  );
};

export default ProceedWithPayment;
